// src/components/Atelier3/Activite1/ThreatRadarVisualization.js
import React, { useState, useEffect, useRef, useImperativeHandle } from 'react';
import { Users, Building, Shield, AlertCircle, ZoomIn, ZoomOut, RefreshCw, Move, Download } from 'lucide-react';
import useAtelier3Store from '../../../stores/useAtelier3Store';
import { showErrorToast } from '../../../utils/toastUtils';

const ThreatRadarVisualization = React.forwardRef(({
  stakeholders,
  thresholds,
  onUpdateThresholds,
  onSelectStakeholder,
  isLoading,
  isThresholdEditing,
  setIsThresholdEditing,
  onVisualizationReady
}, ref) => {

  const [selectedStakeholder, setSelectedStakeholder] = useState(null);
  const [editedThresholds, setEditedThresholds] = useState({ ...thresholds });
  const [hoveredStakeholder, setHoveredStakeholder] = useState(null);
  const [suggestionPreview, setSuggestionPreview] = useState(null);

  // Sync editedThresholds with current thresholds when editing panel opens
  useEffect(() => {
    if (isThresholdEditing) {
      setEditedThresholds({ ...thresholds });
    }
  }, [isThresholdEditing]);
  const svgRef = useRef(null);

  // SVG dimensions
  const width = 800;
  const height = 800;
  const centerX = width / 2;
  const centerY = height / 2;
  const maxRadius = Math.min(width, height) / 2 - 60;

  // Zoom and pan state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPanPoint, setStartPanPoint] = useState({ x: 0, y: 0 });
  const [isExporting, setIsExporting] = useState(false);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(3, prev + 0.2));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(0.5, prev - 0.2));

  const handleResetView = () => {
    setZoomLevel(1);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsPanning(true);
      setStartPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      const dx = (e.clientX - startPanPoint.x) / zoomLevel;
      const dy = (e.clientY - startPanPoint.y) / zoomLevel;
      setPanOffset(prev => ({ x: prev.x + dx, y: prev.y + dy }));
      setStartPanPoint({ x: e.clientX, y: e.clientY });
    }
  };

  const handleMouseUp = () => setIsPanning(false);
  const handleMouseLeave = () => setIsPanning(false);

  const getThresholdRadius = (thresholdValue) => {
    const maxThreshold = thresholds.danger * 1.2;
    const proportion = 1 - (thresholdValue / maxThreshold);
    return maxRadius * proportion;
  };

  // String Hash function to handle string UUIDs and distribute dots nicely
  const hashString = (str) => {
    let hash = 0;
    const s = String(str);
    for (let i = 0; i < s.length; i++) {
      hash = Math.imul(31, hash) + s.charCodeAt(i) | 0;
    }
    return Math.abs(hash);
  };

  const getStakeholderPosition = (stakeholder) => {
    const threatLevel = stakeholder.threatLevel || 0;
    let radius = getThresholdRadius(threatLevel);
    const minRadius = maxRadius * 0.15;

    // Hash the ID (or name as fallback) to get a deterministic but scattered integer
    const hash = hashString(stakeholder.id || stakeholder.name);

    // Jitter the radius slightly based on the hash so same-threat-level dots don't overlap completely
    const radialVariation = ((hash % 100) / 100 - 0.5) * (maxRadius * 0.08); // +/- 4% variance
    radius = Math.max(minRadius, radius + radialVariation);

    // Use Golden Ratio for perfect spiral distribution, eliminating straight lines
    const goldenRatio = 0.618033988749895;
    const angle = (hash * goldenRatio) * 2 * Math.PI;

    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);

    return { x, y, threatLevel, angle, radius };
  };

  const calculateFiabiliteCyber = (stakeholder) => (stakeholder.cyberMaturity || 1) * (stakeholder.trust || 1);
  const calculateExposition = (stakeholder) => (stakeholder.dependency || 1) * (stakeholder.penetration || 1);

  const getFiabiliteCyberColor = (stakeholder) => {
    const fiabiliteCyber = calculateFiabiliteCyber(stakeholder);
    if (fiabiliteCyber < 4) return '#EF4444';
    if (fiabiliteCyber >= 4 && fiabiliteCyber <= 5) return '#F97316';
    if (fiabiliteCyber >= 6 && fiabiliteCyber <= 7) return '#3B82F6';
    return '#10B981';
  };

  const getExpositionSize = (stakeholder) => {
    const exposition = calculateExposition(stakeholder);
    if (exposition < 3) return 20;
    if (exposition >= 3 && exposition <= 6) return 28;
    if (exposition >= 7 && exposition <= 9) return 36;
    return 44;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'client': return <Users size={16} />;
      case 'partner': return <Building size={16} />;
      case 'provider': return <Shield size={16} />;
      case 'technical': return <AlertCircle size={16} />;
      case 'business': return <Building size={16} />;
      case 'subsidiary': return <Building size={16} />;
      default: return <Users size={16} />;
    }
  };

  const getThreatLevelColor = (level, opacity = 1) => {
    let color;
    if (level >= thresholds.danger) color = "#EF4444";
    else if (level >= thresholds.control) color = "#F59E0B";
    else if (level >= thresholds.watch) color = "#10B981";
    else color = "#64748B";

    if (opacity !== 1) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }
    return color;
  };

  const handleThresholdChange = (e) => {
    const { name, value } = e.target;
    setEditedThresholds(prev => ({ ...prev, [name]: parseFloat(value) }));
  };

  const handleSaveThresholds = () => {
    onUpdateThresholds(editedThresholds);
    setIsThresholdEditing(false);
  };

  const handleCancelThresholds = () => {
    setEditedThresholds({ ...thresholds });
    setIsThresholdEditing(false);
  };

  const handleStakeholderClick = (stakeholder) => {
    setSelectedStakeholder(stakeholder);
    if (onSelectStakeholder) onSelectStakeholder(stakeholder);
  };

  const exportToPNG = async () => {
    setIsExporting(true);
    try {
      const imageDataUrl = await generateVisualizationImage();
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `stakeholder-radar-${new Date().toISOString().split('T')[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting to PNG:', error);
      showErrorToast('Erreur lors de l\'export en PNG');
    } finally {
      setIsExporting(false);
    }
  };

  const generateVisualizationImage = async () => {
    if (!svgRef.current) throw new Error('SVG element not found');

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const padding = 40;
    const radarSize = width;
    const legendWidth = 300;
    const titleHeight = 80;
    const totalWidth = radarSize + legendWidth + (padding * 3);
    const totalHeight = radarSize + titleHeight + (padding * 2);

    canvas.width = totalWidth;
    canvas.height = totalHeight;

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalWidth, totalHeight);

    const svgToImage = (svgElement) => {
      return new Promise((resolve, reject) => {
        try {
          const svgData = new XMLSerializer().serializeToString(svgElement);
          const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const url = URL.createObjectURL(svgBlob);

          const img = new Image();
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
          };
          img.onerror = reject;
          img.src = url;
        } catch (error) {
          reject(error);
        }
      });
    };

    const radarImg = await svgToImage(svgRef.current);

    ctx.fillStyle = '#1e40af';
    ctx.font = 'bold 24px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Radar des Parties Prenantes - Workshop 3', totalWidth / 2, padding + 20);

    ctx.fillStyle = '#6b7280';
    ctx.font = '14px Arial';
    ctx.fillText(new Date().toLocaleString(), totalWidth / 2, padding + 45);

    const radarX = padding;
    const radarY = padding + titleHeight;
    ctx.drawImage(radarImg, radarX, radarY, radarSize, radarSize);

    const legendX = radarX + radarSize + padding;
    const legendY = radarY + 20;

    ctx.fillStyle = '#374151';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'left';
    ctx.fillText('Légende', legendX, legendY);

    let currentY = legendY + 30;

    ctx.font = 'bold 14px Arial';
    ctx.fillText('Zones de Menace:', legendX, currentY);
    currentY += 20;

    const zones = [
      { color: '#FFEBEE', border: '#EF4444', text: 'Zone de Danger' },
      { color: '#FFF8E1', border: '#F59E0B', text: 'Zone de Contrôle' },
      { color: '#E8F5E9', border: '#10B981', text: 'Zone de Veille' }
    ];

    zones.forEach(zone => {
      ctx.fillStyle = zone.color;
      ctx.fillRect(legendX, currentY - 10, 15, 15);
      ctx.strokeStyle = zone.border;
      ctx.strokeRect(legendX, currentY - 10, 15, 15);
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.fillText(zone.text, legendX + 20, currentY);
      currentY += 20;
    });

    currentY += 10;
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Types de Parties Prenantes:', legendX, currentY);
    currentY += 20;

    ctx.fillStyle = '#6B7280';
    ctx.beginPath();
    ctx.arc(legendX + 7, currentY - 5, 7, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillStyle = '#374151';
    ctx.font = '12px Arial';
    ctx.fillText('Externe', legendX + 20, currentY);
    currentY += 20;

    ctx.fillStyle = '#6B7280';
    ctx.save();
    ctx.translate(legendX + 7, currentY - 5);
    ctx.rotate(Math.PI / 4);
    ctx.fillRect(-5, -5, 10, 10);
    ctx.restore();
    ctx.fillStyle = '#374151';
    ctx.font = '12px Arial';
    ctx.fillText('Interne', legendX + 20, currentY);
    currentY += 30;

    ctx.font = 'bold 14px Arial';
    ctx.fillText('Fiabilité Cyber (Couleur):', legendX, currentY);
    currentY += 20;

    const reliability = [
      { color: '#EF4444', text: 'Faible (< 4)' },
      { color: '#F97316', text: 'Moyenne (4-5)' },
      { color: '#3B82F6', text: 'Bonne (6-7)' },
      { color: '#10B981', text: 'Excellente (> 7)' }
    ];

    reliability.forEach(item => {
      ctx.fillStyle = item.color;
      ctx.beginPath();
      ctx.arc(legendX + 7, currentY - 5, 7, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.fillText(item.text, legendX + 20, currentY);
      currentY += 18;
    });

    currentY += 10;
    ctx.font = 'bold 14px Arial';
    ctx.fillText('Exposition (Taille):', legendX, currentY);
    currentY += 20;

    const exposure = [
      { size: 3, text: 'Minimale (< 3)' },
      { size: 5, text: 'Modérée (3-6)' },
      { size: 7, text: 'Élevée (7-9)' },
      { size: 9, text: 'Critique (> 9)' }
    ];

    exposure.forEach(item => {
      ctx.fillStyle = '#9CA3AF';
      ctx.beginPath();
      ctx.arc(legendX + 10, currentY - 5, item.size, 0, 2 * Math.PI);
      ctx.fill();
      ctx.fillStyle = '#374151';
      ctx.font = '12px Arial';
      ctx.fillText(item.text, legendX + 25, currentY);
      currentY += 20;
    });

    return canvas.toDataURL('image/png');
  };

  useImperativeHandle(ref, () => ({
    generateVisualizationImage: async () => {
      if (!svgRef.current) throw new Error('SVG element not available');
      return await generateVisualizationImage();
    }
  }), []);

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-4 text-gray-600">Chargement de la visualisation...</p>
      </div>
    );
  }

  if (stakeholders.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="bg-blue-50 p-4 rounded-full inline-flex items-center justify-center mb-4">
          <Users size={32} className="text-blue-500" />
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune partie prenante</h3>
        <p className="text-gray-600 mb-4">
          Ajoutez des parties prenantes pour visualiser la cartographie de l'écosystème.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full">
      <div className="relative h-full bg-white rounded-lg shadow-sm border border-slate-200 p-2">

        {isThresholdEditing && (
          <div className="absolute top-12 right-2 bg-white p-4 rounded-lg shadow-md border border-gray-200 z-20 w-64">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Modifier les seuils</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Seuil de danger</label>
                <input
                  type="number"
                  name="danger"
                  value={editedThresholds.danger}
                  onChange={handleThresholdChange}
                  step="0.1"
                  min="0"
                  className="w-full p-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Seuil de contrôle</label>
                <input
                  type="number"
                  name="control"
                  value={editedThresholds.control}
                  onChange={handleThresholdChange}
                  step="0.1"
                  min="0"
                  className="w-full p-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Seuil de veille</label>
                <input
                  type="number"
                  name="watch"
                  value={editedThresholds.watch}
                  onChange={handleThresholdChange}
                  step="0.1"
                  min="0"
                  className="w-full p-1.5 text-sm border border-gray-300 rounded"
                />
              </div>
            </div>
            <div className="mt-3 pt-2 border-t border-dashed border-blue-200">
              <button
                onClick={() => {
                  const suggested = useAtelier3Store.getState().getEbiosensSuggestedThresholds();
                  if (!suggested) return;
                  const total = stakeholders.length;
                  const countInZone = (zone) => stakeholders.filter(s => {
                    const tl = s.threatLevel;
                    if (zone === 'danger') return tl >= suggested.danger;
                    if (zone === 'control') return tl >= suggested.control && tl < suggested.danger;
                    if (zone === 'watch') return tl >= suggested.watch && tl < suggested.control;
                    return tl < suggested.watch;
                  }).length;
                  setSuggestionPreview({
                    ...suggested,
                    counts: {
                      danger: countInZone('danger'),
                      control: countInZone('control'),
                      watch: countInZone('watch'),
                      outside: countInZone('outside'),
                    },
                    total,
                  });
                }}
                className="w-full px-2 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
              >
                Suggestion Ebiosens — distribution 10 % / 40 % / 40 % / 10 %
              </button>

              {suggestionPreview && (
                <div className="mt-2 p-3 bg-blue-50 rounded border border-blue-200 space-y-2 text-xs">
                  <div className="font-semibold text-blue-800">Répartition suggérée</div>
                  <div className="flex justify-between text-red-700">
                    <span>Danger ≥ {suggestionPreview.danger.toFixed(2)}</span>
                    <span>{suggestionPreview.counts.danger} PP ({Math.round(suggestionPreview.counts.danger / suggestionPreview.total * 100)}%)</span>
                  </div>
                  <div className="flex justify-between text-amber-700">
                    <span>Contrôle ≥ {suggestionPreview.control.toFixed(2)}</span>
                    <span>{suggestionPreview.counts.control} PP ({Math.round(suggestionPreview.counts.control / suggestionPreview.total * 100)}%)</span>
                  </div>
                  <div className="flex justify-between text-green-700">
                    <span>Veille ≥ {suggestionPreview.watch.toFixed(2)}</span>
                    <span>{suggestionPreview.counts.watch} PP ({Math.round(suggestionPreview.counts.watch / suggestionPreview.total * 100)}%)</span>
                  </div>
                  <div className="flex justify-between text-slate-500">
                    <span>Hors périmètre &lt; {suggestionPreview.watch.toFixed(2)}</span>
                    <span>{suggestionPreview.counts.outside} PP ({Math.round(suggestionPreview.counts.outside / suggestionPreview.total * 100)}%)</span>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => {
                        setEditedThresholds(suggestionPreview);
                        setSuggestionPreview(null);
                      }}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                    >
                      Appliquer les seuils suggérés
                    </button>
                    <button
                      onClick={() => setSuggestionPreview(null)}
                      className="flex-1 px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded hover:bg-slate-50"
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end mt-4 space-x-2">
              <button onClick={handleCancelThresholds} className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={handleSaveThresholds} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                Appliquer
              </button>
            </div>
          </div>
        )}

        <div className="absolute top-2 left-2 z-10 bg-white rounded-md shadow-sm border border-gray-200 p-1 flex items-center">
          <button onClick={handleZoomOut} className="p-1 text-gray-600 hover:bg-gray-100 rounded" disabled={zoomLevel <= 0.5} title="Zoom out">
            <ZoomOut size={16} />
          </button>
          <span className="mx-1 text-xs font-medium">{Math.round(zoomLevel * 100)}%</span>
          <button onClick={handleZoomIn} className="p-1 text-gray-600 hover:bg-gray-100 rounded" disabled={zoomLevel >= 3} title="Zoom in">
            <ZoomIn size={16} />
          </button>
          <button onClick={handleResetView} className="ml-1 p-1 text-gray-600 hover:bg-gray-100 rounded" disabled={zoomLevel === 1 && panOffset.x === 0 && panOffset.y === 0} title="Reset view">
            <RefreshCw size={16} />
          </button>
          <div className="ml-1 p-1 text-gray-600 flex items-center" title="Drag to move">
            <Move size={16} />
            <span className="ml-1 text-xs">Drag</span>
          </div>
        </div>

        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={exportToPNG}
            disabled={isExporting}
            className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${isExporting ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
            title="Exporter en PNG"
          >
            <Download size={16} className="mr-2" />
            {isExporting ? 'Export...' : 'PNG'}
          </button>
        </div>

        <div className="w-full h-full overflow-hidden">
          <svg
            ref={svgRef}
            width="100%"
            height="100%"
            viewBox={`0 0 ${width} ${height}`}
            className="mx-auto max-w-full max-h-full cursor-move"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{
              transform: `scale(${zoomLevel}) translate(${panOffset.x}px, ${panOffset.y}px)`,
              transformOrigin: 'center',
              transition: 'transform 0.1s ease-out'
            }}
          >
            <defs>
              <marker
                id="mitigation-arrow"
                viewBox="0 0 10 10"
                refX="6"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 1.5 L 8 5 L 0 8.5 z" fill="#3B82F6" />
              </marker>
            </defs>

            {[2, 4, 6, 8].map(level => {
              const radius = getThresholdRadius(level);
              return (
                <g key={`grid-${level}`}>
                  <circle cx={centerX} cy={centerY} r={radius} fill="none" stroke="rgba(100, 116, 139, 0.8)" strokeWidth="1.5" strokeDasharray="5,4" />
                </g>
              );
            })}

            <circle cx={centerX} cy={centerY} r={getThresholdRadius(thresholds.watch)} fill="rgba(229, 247, 235, 0.4)" stroke="#10B981" strokeWidth="2" />
            <circle cx={centerX} cy={centerY} r={getThresholdRadius(thresholds.control)} fill="rgba(254, 243, 199, 0.4)" stroke="#F59E0B" strokeWidth="2" />
            <circle cx={centerX} cy={centerY} r={getThresholdRadius(thresholds.danger)} fill="rgba(254, 226, 226, 0.4)" stroke="#EF4444" strokeWidth="2" />

            <circle cx={centerX} cy={centerY} r={12} fill="#334155" stroke="#475569" strokeWidth="1.5" />
            <text x={centerX} y={centerY + 30} textAnchor="middle" fill="#334155" fontSize="11" fontWeight="medium">Objet de l'étude</text>

            <rect x={centerX - 50} y={centerY - getThresholdRadius(thresholds.danger) - 18} width={100} height={20} rx={10} fill="rgba(254, 226, 226, 0.8)" stroke="#EF4444" strokeWidth="1" />
            <text x={centerX} y={centerY - getThresholdRadius(thresholds.danger) - 5} textAnchor="middle" fill="#B91C1C" fontSize="12" fontWeight="bold">Zone de Danger</text>

            <rect x={centerX - 55} y={centerY - getThresholdRadius(thresholds.control) - 18} width={110} height={20} rx={10} fill="rgba(254, 243, 199, 0.8)" stroke="#F59E0B" strokeWidth="1" />
            <text x={centerX} y={centerY - getThresholdRadius(thresholds.control) - 5} textAnchor="middle" fill="#B45309" fontSize="12" fontWeight="bold">Zone de Contrôle</text>

            <rect x={centerX - 50} y={centerY - getThresholdRadius(thresholds.watch) - 18} width={100} height={20} rx={10} fill="rgba(229, 247, 235, 0.8)" stroke="#10B981" strokeWidth="1" />
            <text x={centerX} y={centerY - getThresholdRadius(thresholds.watch) - 5} textAnchor="middle" fill="#047857" fontSize="12" fontWeight="bold">Zone de Veille</text>

            {stakeholders.map(stakeholder => {
              const { x, y } = getStakeholderPosition(stakeholder);
              const color = getFiabiliteCyberColor(stakeholder);
              const pointSize = getExpositionSize(stakeholder);
              const isInternal = stakeholder.type === 'internal';
              const isHovered = hoveredStakeholder?.id === stakeholder.id;
              const threatColor = getThreatLevelColor(stakeholder.threatLevel);
              const label = stakeholder.name || '—';
              const labelW = Math.max(40, (label.length * 5) + 8);

              // Calculate initial movement positions if initialValues is provided
              let initialPos = null;
              let hasMovement = false;
              let x2 = x;
              let y2 = y;

              if (stakeholder.initialValues) {
                initialPos = getStakeholderPosition({
                  ...stakeholder,
                  threatLevel: stakeholder.initialValues.threatLevel
                });

                const dx = x - initialPos.x;
                const dy = y - initialPos.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist > 4) {
                  hasMovement = true;
                  const shrinkFactor = (pointSize / 2) + 6; // dot radius + marker offset space
                  x2 = x - (dx / dist) * shrinkFactor;
                  y2 = y - (dy / dist) * shrinkFactor;
                }
              }

              return (
                <g
                  key={stakeholder.id}
                  onClick={() => handleStakeholderClick(stakeholder)}
                  onMouseEnter={() => setHoveredStakeholder(stakeholder)}
                  onMouseLeave={() => setHoveredStakeholder(null)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* INVISIBLE CAPTURE AREA: This solves the hover flickering issue completely */}
                  <circle cx={x} cy={y} r={Math.max(pointSize + 15, 30)} fill="transparent" />

                  {/* GLOW EFFECT: Added pointerEvents: 'none' so it doesn't steal the mouse from the invisible area */}
                  {isHovered && <circle cx={x} cy={y} r={pointSize + 6} fill="rgba(59, 130, 246, 0.3)" stroke="#3b82f6" strokeWidth={1} strokeDasharray="3,3" style={{ pointerEvents: 'none' }} />}

                  {/* Connecting Arrow showing trajectory from initial risk position */}
                  {hasMovement && initialPos && (
                    <g style={{ pointerEvents: 'none' }}>
                      {/* Small dashed circle representing initial position */}
                      <circle
                        cx={initialPos.x}
                        cy={initialPos.y}
                        r={pointSize / 3}
                        fill="none"
                        stroke="#94A3B8"
                        strokeWidth="1.5"
                        strokeDasharray="2,2"
                        opacity="0.6"
                      />
                      {/* Dashed line pointing to new position */}
                      <line
                        x1={initialPos.x}
                        y1={initialPos.y}
                        x2={x2}
                        y2={y2}
                        stroke="#3B82F6"
                        strokeWidth="2"
                        strokeDasharray="4,2"
                        markerEnd="url(#mitigation-arrow)"
                        opacity="0.75"
                      />
                    </g>
                  )}

                  <circle cx={x} cy={y} r={pointSize + 2} fill="none" stroke={threatColor} strokeWidth={2.5} strokeOpacity={0.6} style={{ pointerEvents: 'none' }} />

                  {isInternal ?
                    <rect x={x - pointSize / 2} y={y - pointSize / 2} width={pointSize} height={pointSize} fill={color} stroke={threatColor} strokeWidth={1.5} transform={`rotate(45, ${x}, ${y})`} style={{ pointerEvents: 'none' }} />
                    :
                    <circle cx={x} cy={y} r={pointSize / 2} fill={color} stroke={threatColor} strokeWidth={1.5} style={{ pointerEvents: 'none' }} />
                  }

                  <rect x={x - labelW / 2} y={y + pointSize + 2} width={labelW} height={16} rx={8} fill="rgba(255, 255, 255, 0.9)" stroke={color} strokeWidth={0.5} style={{ pointerEvents: 'none' }} />
                  <text x={x} y={y + pointSize + 14} textAnchor="middle" fill="#1E293B" fontSize="10" fontWeight="medium" style={{ pointerEvents: 'none' }}>{label}</text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* TOOLTIP: Added 'pointer-events-none' to prevent it from resetting hover states if it accidentally overlaps the mouse cursor */}
        {hoveredStakeholder && (
          <div
            className="absolute top-2 right-2 bg-white p-3 rounded-lg shadow-lg border border-gray-200 z-30 w-72 pointer-events-none"
            style={{ borderColor: getFiabiliteCyberColor(hoveredStakeholder) }}
          >
            <div className="flex items-center mb-2">
              <div className="p-1.5 rounded-full mr-2" style={{ backgroundColor: getFiabiliteCyberColor(hoveredStakeholder) }}>
                {getCategoryIcon(hoveredStakeholder.category)}
              </div>
              <div>
                <h3 className="font-medium text-gray-900">{hoveredStakeholder.name}</h3>
                <p className="text-xs text-gray-500">{hoveredStakeholder.type === 'internal' ? 'Interne' : 'Externe'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs mb-2">
              <div><span className="text-gray-500">Dépendance:</span><span className="ml-1 font-medium">{hoveredStakeholder.dependency}</span></div>
              <div><span className="text-gray-500">Pénétration:</span><span className="ml-1 font-medium">{hoveredStakeholder.penetration}</span></div>
              <div><span className="text-gray-500">Maturité:</span><span className="ml-1 font-medium">{hoveredStakeholder.cyberMaturity}</span></div>
              <div><span className="text-gray-500">Confiance:</span><span className="ml-1 font-medium">{hoveredStakeholder.trust}</span></div>
            </div>

            <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Fiabilité Cyber:</span>
                <span className="font-bold text-sm px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: getFiabiliteCyberColor(hoveredStakeholder) }}>
                  {calculateFiabiliteCyber(hoveredStakeholder).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Exposition:</span>
                <span className="font-bold text-sm px-2 py-0.5 rounded-full bg-gray-100 text-gray-800">
                  {calculateExposition(hoveredStakeholder).toFixed(1)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Niveau de menace:</span>
                <span className="font-bold text-sm px-2 py-0.5 rounded-full" style={{ backgroundColor: getThreatLevelColor(hoveredStakeholder.threatLevel, 0.2), color: getThreatLevelColor(hoveredStakeholder.threatLevel) }}>
                  {hoveredStakeholder.threatLevel?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

ThreatRadarVisualization.displayName = 'ThreatRadarVisualization';

export default ThreatRadarVisualization;